import React, { useState } from "react";
import { Button, Form, Row, Col, Image } from "react-bootstrap";
import Modal from "../common/components/ThemedModal";
import {
	CredentialResponse,
	GoogleCredentialResponse,
	GoogleLogin,
} from "@react-oauth/google";
import { RootState } from "../../store/store";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setShowLoginModal } from "../../store/slices/userSlice";
import Logo from "../common/Logo";
import { jwtDecode } from "jwt-decode";
import { getUserByEmail } from "../../services/userService";
import { signUp } from "../../services/mailChimpService";
import {
	googleLoginThunk,
	loginThunk,
	signUpThunk,
} from "../../store/thunks/authThunk";

interface LoginSignupModalProps {}

enum LoginSignupMode {
	Login = "login",
	Signup = "signup",
}

const LoginSignupModal: React.FC<LoginSignupModalProps> = () => {
	const dispatch = useAppDispatch();
	const { isLoggedIn, showLoginModal } = useAppSelector(
		(state: RootState) => state.user,
	);

	const [mode, setMode] = useState<LoginSignupMode>(LoginSignupMode.Login);
	const [googlCredential, setGoogleCredential] =
		useState<CredentialResponse | null>(null);
	const [username, setUsername] = useState("");
	const [email, setEmail] = useState("");
	const [confirmEmail, setConfirmEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [acceptTerms, setAcceptTerms] = useState(false);
	const [subscribe, setSubscribe] = useState(false);

	const handleSwitchMode = () =>
		setMode(
			mode === LoginSignupMode.Login
				? LoginSignupMode.Signup
				: LoginSignupMode.Login,
		);

	const isReservedEmailDomain = (email: string) => {
		const domain = email.split("@")[1];
		return domain === "gmail.com"; // Add other domains as necessary
	};

	const handleLogin = async (e: any) => {
		e.preventDefault();
		try {
			// if (isReservedEmailDomain(email)) {
			// 	alert("Please use Google Sign In for Gmail accounts.");
			// 	return;
			// }

			const result = await dispatch(loginThunk({ email, password }));

			if (!loginThunk.fulfilled.match(result)) {
				alert("Login failed. Please check your credentials.");
				return;
			}

			if (result.payload) {
				dispatch(setShowLoginModal(false));
				clearForm();
			}
		} catch (error) {
			console.error("Error signing in:", error);
			alert("An error occurred while signing in." + error);
		}
	};

	const clearForm = () => {
		setUsername("");
		setEmail("");
		setConfirmEmail("");
		setPassword("");
		setConfirmPassword("");
		setAcceptTerms(false);
		setSubscribe(false);
	};

	const extractUserDataFromResponse = (
		credentialResponse: CredentialResponse,
	) => {
		if (!credentialResponse.credential) {
			throw new Error("No credential found in the response");
		}
		const decodedToken: any = jwtDecode(credentialResponse.credential);
		const userData = {
			username: decodedToken.name, // The JWT standard claims might be different; adjust accordingly
			email: decodedToken.email,
			profilePic: decodedToken.picture, // This might vary based on the token's structure
		};
		return userData;
	};

	const validateCheckboxes = () => {
		if (!acceptTerms) {
			alert("Please accept the terms and conditions.");
			return false;
		}
		return true;
	};

	const handleGoogleSignIn = async (credentialResponse: CredentialResponse) => {
		try {
			if (!credentialResponse.credential) {
				console.error("No credential found in the response");
				return;
			}

			const userData = extractUserDataFromResponse(credentialResponse);

			const res = await dispatch(
				googleLoginThunk(credentialResponse.credential),
			);
			if (!googleLoginThunk.fulfilled.match(res)) {
				alert("Login failed");
				return;
			}
			dispatch(setShowLoginModal(false));
		} catch (error) {
			console.error("Error signing in with Google:", error);
		}
	};

	const handleSignup = async () => {
		if (isReservedEmailDomain(email)) {
			alert("Please use Google Sign In for Gmail accounts.");
			return;
		}

		if (!validateCheckboxes()) {
			return;
		}

		if (email !== confirmEmail) {
			alert("Emails do not match");
			return;
		}

		if (password !== confirmPassword) {
			alert("Passwords do not match");
			return;
		}

		if (username === "") {
			alert("Please enter a username");
			return;
		}

		// if (subscribe) {
		// 	signUp(email);
		// }

		try {
			const res = await dispatch(
				signUpThunk({
					username: username,
					email: email,
					password: password,
					agreeToTerms: acceptTerms,
					agreeToMarketing: subscribe,
				}),
			);

			if (!signUpThunk.fulfilled.match(res)) {
				alert("Sign-up failed. If your account already exists, please login.");
				return;
			}

			dispatch(setShowLoginModal(false));
			clearForm();
		} catch (error) {
			console.error("Error signing up:", error);
			alert("An error occurred while signing up.");
		}
	};

	const onHide = () => {
		dispatch(setShowLoginModal(false));
	};

	return (
		<Modal show={showLoginModal} onHide={onHide} centered>
			<Modal.Body className="p-4 rounded-2">
				<Row className="align-items-center mb-4">
					<Col>
						<h4 className="text-white mb-0 fs-1 fw-bold">
							{mode === LoginSignupMode.Login ? "LOGIN" : "SIGN UP"}
						</h4>
					</Col>
					<Col className="text-end">
						<Logo />
					</Col>
				</Row>

				<Form className="text-light">
					{mode === LoginSignupMode.Signup && (
						<Form.Group className="mb-3">
							<Form.Control
								type="text"
								placeholder="Username"
								isValid={username !== ""}
								value={username}
								onChange={(e) => setUsername(e.target.value)}
							/>
						</Form.Group>
					)}
					<Form.Group className="mb-3">
						<Form.Control
							type="email"
							placeholder="Email"
							isValid={email !== ""}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</Form.Group>
					{mode === LoginSignupMode.Signup && (
						<Form.Group className="mb-3">
							<Form.Control
								type="email"
								placeholder="Confirm Email"
								isInvalid={
									mode === LoginSignupMode.Signup &&
									email !== confirmEmail &&
									email !== ""
								}
								isValid={
									mode === LoginSignupMode.Signup && email === confirmEmail
								}
								value={confirmEmail}
								onChange={(e) => setConfirmEmail(e.target.value)}
							/>
						</Form.Group>
					)}
					<Form.Group className="mb-3">
						<Form.Control
							type="password"
							placeholder="Password"
							isValid={password !== ""}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					</Form.Group>
					{mode === LoginSignupMode.Signup && (
						<Form.Group className="mb-3">
							<Form.Control
								type="password"
								placeholder="Confirm Password"
								isInvalid={
									mode === LoginSignupMode.Signup &&
									password !== confirmPassword
								}
								isValid={
									mode === LoginSignupMode.Signup &&
									password === confirmPassword
								}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
							/>
						</Form.Group>
					)}
					{mode === LoginSignupMode.Login && (
						<div className="mb-3">
							<a href="/forgotpassword" className="text-success">
								Forgot password?
							</a>
						</div>
					)}
					{mode === LoginSignupMode.Signup && (
						<>
							<Form.Check
								className="mb-2"
								type="checkbox"
								label={
									<span>
										I agree to the{" "}
										<a
											href="/termsandconditions"
											className="text-info"
											onClick={(e) => {
												e.stopPropagation();
											}}
										>
											terms and conditions
										</a>
									</span>
								}
								checked={acceptTerms}
								onChange={(e) => setAcceptTerms(e.target.checked)}
							/>
							<Form.Check
								className="mb-3"
								type="checkbox"
								label="I DO WANT to subscribe to marketing updates, competitions team updates & pro tips"
								checked={subscribe}
								onChange={(e) => setSubscribe(e.target.checked)}
							/>
						</>
					)}
					<Button
						variant="success"
						className="w-100 mb-3 text-light fw-bold"
						disabled={
							mode === LoginSignupMode.Signup
								? !username ||
								  !email ||
								  !password ||
								  password !== confirmPassword ||
								  email !== confirmEmail ||
								  !acceptTerms
								: !email || !password
						}
						onClick={
							mode === LoginSignupMode.Login ? handleLogin : handleSignup
						}
					>
						{mode === LoginSignupMode.Login ? "LOGIN" : "SIGN UP"}
					</Button>
					<div className="text-center mb-2">OR</div>
					{/* <Button
						variant="outline-light"
						className="w-100 mb-3"
						onClick={onGoogleLogin}
					> */}
					<div className="d-flex flex-row justify-content-center w-100 mb-4">
						<GoogleLogin
							onSuccess={handleGoogleSignIn}
							onError={() => {
								alert("Login failed");
							}}
							type="standard"
							size="large"
							shape="pill"
							theme="filled_black"
							useOneTap={true}
							// auto_select={true}
						/>{" "}
					</div>
					{/* </Button> */}
					<div className="text-center">
						{mode === LoginSignupMode.Login ? (
							<span>
								Don't have an account?{" "}
								<a href="#" onClick={handleSwitchMode} className="text-info">
									Sign Up
								</a>
							</span>
						) : (
							<span>
								Already have an account?{" "}
								<a href="#" onClick={handleSwitchMode} className="text-info">
									Login
								</a>
							</span>
						)}
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

export default LoginSignupModal;
